<template>
  <div class="step-1-container">
    <v-row>
      <v-col>
        <title-description
          hide-status
          :title.sync="highlight.title"
          :description.sync="highlight.description"
          :title-error="$v.highlight.title.$error"
          :title-error-message="errorTitle"
          :description-error="$v.highlight.description.$error"
          :description-error-message="errorDescription"
          :disable="activeHighlight"
          :disable-title="finishedHighlight"
          :disable-description="finishedHighlight"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="isOfferSchedulePermissionActive" cols="6" class="d-flex">
        <offer-schedule-card
          :offer-schedule="offerSchedule"
          :has-selected-products="highlight.products && highlight.products.length > 0 && !isOfferSchedule && isEditable"
          show-products-warning
          :disabled="(highlight.products && highlight.products.length > 0 && !isOfferSchedule) || !isEditable"
          :block-offers-types="blockOffersTypes"
          hide-personalized
        />
      </v-col>
      <v-col cols="6">
        <products-configuration
          v-if="!$getFirstValue()"
          class="mb-6 fill-height"
          :products.sync="highlightProductsAndGroups"
          :is-offer-schedule="isOfferSchedule"
          :disabled="!isEditable"
          show-offer-schedule-warning
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="isOfferSchedulePermissionActive" cols="6" class="d-flex">
        <expiration-date-card
          :highlight.sync="highlight"
          :start-date.sync="highlight.initialDate"
          :start-date-error="errorInitialDate"
          :end-date.sync="highlight.finalDate"
          :is-offer-schedule="isOfferSchedule"
          :disable-fields="finishedHighlight"
          :edit-only-end-date="activeHighlight"
        />
      </v-col>
      <v-col cols="6">
        <units-configuration :error="$v.highlight.units.$error" :selected-units.sync="highlight.units" :disabled="finishedHighlight">
          <template v-slot:additional-content>
            <mf-info-card class="mt-4" variant="warning" icon="warning" :v-html="infoCardDescription" />
          </template>
        </units-configuration>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="d-flex">
        <pick-highlight-model />
      </v-col>
    </v-row>

    <mf-action-buttons :primary-button="primaryButton" :secondary-button="secondaryButton" :cancel-button="cancelButton" />
  </div>
</template>

<script>
import { required, minLength, maxLength, minValue } from 'vuelidate/lib/validators'
import { notOnlySpace, isFuture } from '@/helpers/validators.js'
import { highlightOffer } from '../../../../mixins/highlight-offer'
import { DateTime } from 'luxon'

export default {
  name: 'HighlightStep1',
  components: {
    TitleDescription: () => import('@/modules/campanhas/views/components/edit/TitleDescription.vue'),
    OfferScheduleCard: () => import('@/modules/campanhas/views/components/edit/OfferScheduleCard.vue'),
    ProductsConfiguration: () => import('@/modules/campanhas/views/components/edit/ProductsConfiguration.vue'),
    UnitsConfiguration: () => import('@/modules/mobile/banners/views/components/UnitsConfiguration.vue'),
    ExpirationDateCard: () => import('./ExpirationDateCard.vue'),
    PickHighlightModel: () => import('./PickHighlightModel.vue'),
  },
  mixins: [highlightOffer],
  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
    offerSchedule: {
      type: Object,
      default: () => ({}),
    },
    isOfferSchedulePermissionActive: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    loading: {
      saveDraft: false,
      validateStep1: false,
    },
    tryingToSaveAsDraft: false,
  }),
  computed: {
    highlightProductsAndGroups: {
      get() {
        const products = this.highlight.products || []
        const groups = (this.highlight.productsGroups || []).map((group) => ({
          ...group,
          isGroup: true,
        }))
        return [...products, ...groups]
      },
      set(value) {
        const products = value.filter((item) => !item.isGroup)
        const productsGroups = value.filter((item) => item.isGroup)
        this.setHighlightKeyValue({ key: 'products', payload: products })
        this.setHighlightKeyValue({ key: 'productsGroups', payload: productsGroups })
      },
    },
    highlight: {
      set(value) {
        this.setHighlightObject(value)
      },
      get() {
        return this.highlightOffer
      },
    },
    primaryButton() {
      return {
        text: 'Continuar',
        action: this.finishedHighlight ? this.changeStep : this.validateStep1,
        isVisible: true,
        isDisabled: this.loading.saveDraft,
        isLoading: this.loading.validateStep1,
      }
    },
    secondaryButton() {
      return {
        text: this.draftHighlight ? 'Salvar e sair' : 'Salvar rascunho',
        action: this.saveDraft,
        isVisible: this.draftHighlight,
        isDisabled: this.anyLoading && !this.loading.saveDraft,
        isLoading: this.loading.saveDraft,
      }
    },
    cancelButton() {
      return {
        text: this.finishedHighlight ? 'Fechar' : 'Cancelar',
        action: this.cancel,
        isVisible: true,
        isDisabled: this.anyLoading,
        isLoading: false,
      }
    },
    anyLoading() {
      return Object.entries(this.loading).some(([key, value]) => value)
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
    infoCardDescription() {
      return '<span  class="warning-message"><b>Atenção: </b>Se atente ao limite de destaques por loja. O limite do modelo de destaque expandido e do modelo de destaque padrão é de 1 (um) por loja.</span>'
    },
    errorInitialDate() {
      if (!this.$v.highlight.initialDate) return ''
      if (this.$v.highlight.initialDate.$error) {
        if (!this.$v.highlight.initialDate.required && !this.$v.highlight.finalDate.required) return 'Selecione uma data inicial e final.'
        else if (!this.$v.highlight.initialDate.isFuture) return 'A data inicial deve ser no futuro'
      } else return ''

      return ''
    },
    errorTitle() {
      if (!this.$v.highlight.title) return ''
      if (this.$v.highlight.title.$error) {
        if (!this.$v.highlight.title.required) return 'Dê um nome para este destaque.'
        else if (!this.$v.highlight.title.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.highlight.title.maxLength) return 'O título possui mais de 70 caracteres.'
        else if (!this.$v.highlight.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    blockOffersTypes() {
      return [
        'dynamic_rule',
        'dynamic_rule_offer_sales',
        'next_buy_cashback',
        'next_buy_cashback_offer_sales',
        'cross_sell',
        'crosssell',
        'cross_sell_offer_sales',
      ]
    },
    errorDescription() {
      if (!this.$v.highlight.description) return ''
      if (this.$v.highlight.description.$error) {
        if (!this.$v.highlight.description.required) return 'Dê uma descrição para este destaque.'
        if (!this.$v.highlight.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        if (!this.$v.highlight.description.maxLength) return 'A descrição possui mais de 150 caracteres.'
        if (!this.$v.highlight.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    newHightlight() {
      let path = this.$router.currentRoute.fullPath
      return path.includes('novo')
    },
    isOfferSchedule() {
      return this.campaign.offers_schedule_id.length > 0
    },
    isEditable() {
      return ['DRAFT', 'SCHEDULED'].includes(this.highlightStatus)
    },
    activeHighlight() {
      return this.highlightStatus === 'ACTIVE'
    },
    finishedHighlight() {
      return this.highlightStatus === 'FINISHED'
    },
    draftHighlight() {
      return this.highlightStatus === 'DRAFT'
    },
    scheduledHighlight() {
      return this.highlightStatus === 'SCHEDULED'
    },
    products() {
      return this.highlightOffer.products.map((product) => {
        return {
          sku: product.sku,
          showHome: product.hasOwnProperty('select'),
        }
      })
    },
  },
  watch: {
    offerSchedule: {
      handler(newValue) {
        const { description, name, units, end_date, start_date, _id, offer_sales = [] } = newValue

        const current = DateTime.now()
        const compare_date = DateTime.fromFormat(start_date, 'yyyy-MM-dd')
        let initialDate = start_date
        if (compare_date < current) {
          initialDate = current.toFormat('yyyy-MM-dd')
        }

        const groups = offer_sales.filter((item) => item.offer_sales && item._id)
        const groupSkus = groups.flatMap((group) => group.offer_sales?.map((prod) => prod.sku))

        const flatProducts = offer_sales
          .filter((item) => item.sku && !groupSkus.includes(item.sku))
          .map((item) => ({
            sku: item.sku,
            name: item.name || '',
            showHome: false,
          }))

        const productGroups = groups.map((group) => ({
          _id: group._id,
          name: group.name || 'Grupo sem nome',
          showHome: false,
          isGroup: true,
        }))

        this.highlight = {
          ...this.highlight,
          description: this.highlight.description?.length > 0 ? this.highlight.description : description,
          title: this.highlight.title?.length > 0 ? this.highlight.title : name,
          units: units?.map((unit) => unit.id_erp),
          finalDate: end_date,
          initialDate,
          offersScheduleId: _id,
          products: [...flatProducts],
          productsGroups: [...productGroups],
        }

        this.$nextTick(() => {
          this.setHighlightKeyValue({ key: 'products', payload: [...flatProducts] })
          this.setHighlightKeyValue({ key: 'productsGroups', payload: [...productGroups] })
        })
      },
      deep: true,
    },

    'campaign.products': {
      handler(value) {
        this.setHighlightKeyValue({ payload: value, key: 'products' })
      },
    },
  },
  methods: {
    changeStep() {
      this.$emit('change-step', 2)
    },

    formatProductsAndGroups(items) {
      const products = []
      const productsGroups = []

      items.forEach((item) => {
        if (item?.isGroup) {
          productsGroups.push({
            product_group_id: item._id,
            showHome: !!item.showHome,
          })
        } else {
          products.push({
            sku: item.sku,
            showHome: !!item.showHome,
          })
        }
      })

      return { products, productsGroups }
    },

    async validateStep1() {
      this.loading.validateStep1 = true
      let validateFields = true
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        validateFields = false
        this.scrollWindowToError('.error--text')
      } else if (!this.highlightModelType) {
        validateFields = false
        this.$snackbar({ message: 'Selecione um tipo de modelo.', snackbarColor: 'error' })
      } else if (!this.highlightOffer.products.length && !this.highlightOffer.productsGroups.length) {
        validateFields = false
        this.$snackbar({ message: 'É necessário adicionar ao menos um produto.', snackbarColor: 'error' })
      }

      if (validateFields) {
        const variables = {
          input: {
            id: this.highlight.id,
            finalDate: this.highlight.finalDate,
            initialDate: this.highlight.initialDate,
            type: this.highlight.type,
            units: this.highlight.units,
          },
        }

        const validate = await this.validateHighlightStep1(variables)
        if (validate.valid) {
          this.changeStep()
        } else {
          if (validate.error.code === 'ALREADY_EXISTS_HIGHLIGHT') {
            const { initialDateFound, finalDateFound } = validate.error
            this.$snackbar({
              message: `Já existe um destaque com o modelo selecionado para essas lojas no período de ${DateTime.fromFormat(
                initialDateFound,
                'yyyy-MM-dd'
              ).toFormat('dd/MM/yyyy')} até ${DateTime.fromFormat(finalDateFound, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')}.`,
              snackbarColor: 'error',
            })
          } else {
            this.$snackbar({ message: 'Erro ao continuar, por favor, verifique todos os campos.', snackbarColor: 'error' })
          }
        }
      }
      this.loading.validateStep1 = false
    },

    scrollWindowToError(querySelector) {
      this.$nextTick(() => {
        let element = document.querySelector(querySelector)
        if (element) {
          this.$vuetify.goTo(element, { offset: 200 })
          this.$snackbar({ message: 'Preencha todos os campos obrigatórios.', snackbarColor: 'error' })
        } else {
          this.$snackbar({ message: 'Erro ao continuar, por favor, verifique os campos', snackbarColor: 'error' })
          window.scrollTo(0, 0)
        }
      })
    },

    async saveDraft() {
      this.loading.saveDraft = true
      this.tryingToSaveAsDraft = true
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.scrollWindowToError('.error--text')
      } else {
        this.setHighlightKeyValue({ payload: true, key: 'draft' })

        const { products, productsGroups } = this.formatProductsAndGroups([...this.highlightOffer.products, ...this.highlightOffer.productsGroups])

        const variables = {
          ...this.highlightOffer,
          products,
          productsGroups,
        }

        try {
          const validate = await this.upsertHighlight({ input: variables })
          if (validate) {
            this.$snackbar({ message: 'Rascunho salvo com sucesso', snackbarColor: 'success' })
            this.$router.push('/mobile/destaques').catch(() => {})
          }
        } catch (error) {
          this.$snackbar({ message: 'Erro ao salvar rascunho.', snackbarColor: 'error' })
        }
      }
      this.tryingToSaveAsDraft = false
      this.loading.saveDraft = false
    },

    async cancel() {
      let confirmed = false
      if (!this.finishedHighlight) {
        confirmed = await this.$confirm({
          title: 'Tem certeza?',
          message:
            'Seu destaque ainda não foi salvo e todas as suas alterações recentes serão perdidas ao sair dessa página. Para continuar editando clique em <strong>continuar editando</strong>, para sair sem salvar clique em <strong>descartar alterações</strong>.',
          confirmText: 'Continuar editando',
          cancelText: 'Descartar alterações',
        })
          .then(() => false)
          .catch(() => true)
      } else {
        confirmed = true
      }

      if (confirmed) this.$router.push('/mobile/destaques').catch(() => {})
    },
  },
  validations() {
    if (this.tryingToSaveAsDraft) {
      return {
        highlight: {
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
            maxLength: maxLength(70),
          },
        },
      }
    } else {
      return {
        highlight: {
          units: {
            required,
          },
          initialDate: {
            required,
            ...(!this.isOfferSchedule && ['DRAFT', 'SCHEDULED'].includes(this.highlightStatus) ? { isFuture } : {}),
          },
          finalDate: {
            required,
          },
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
            maxLength: maxLength(70),
          },
          description: {
            required,
            notOnlySpace,
            minLength: minLength(2),
            maxLength: maxLength(150),
          },
          frequency: {
            interval: {
              minValue: minValue(1),
            },
          },
        },
      }
    }
  },
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');

::v-deep .warning-message {
  color: #a26100;
}

::v-deep .v-card {
  border-radius: 8px !important;
}

::v-deep .title {
  color: $cinza-d10;
  font-size: 20px;
  font-weight: 700;
  line-height: 120%; /* 24px */
}
</style>
